import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout, { PageHeader } from "@/components/Layout";
import { api, fmtINR, fmtNum } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Printer, ArrowLeft, CheckCheck, Send, X as XIcon, Trash2, FileText } from "lucide-react";
import ChargesEditor from "@/components/ChargesEditor";
import MezzanineTopView from "@/components/MezzanineTopView";
import PalletBomModal from "@/components/PalletBomModal";

export default function MezzanineDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDealer = user?.role === "dealer";
  const canEditCharges = user?.role === "admin" || user?.role === "employee";
  const canUseBom = user?.role === "admin" || user?.role === "employee";
  const [q, setQ] = useState(null);

  // BOM state
  const [bomOpen, setBomOpen] = useState(false);
  const [bomRows, setBomRows] = useState([]);
  const [bomBusy, setBomBusy] = useState(false);

  useEffect(() => {
    api.get(`/mezzanine/quotations/${id}`).then((r) => setQ(r.data));
  }, [id]);

  const changeStatus = async (status) => {
    const { data } = await api.patch(`/mezzanine/quotations/${id}/status`, { status });
    setQ(data);
    toast.success(`Marked ${status}`);
  };
  const del = async () => {
    if (!window.confirm("Delete this mezzanine quotation?")) return;
    await api.delete(`/mezzanine/quotations/${id}`);
    navigate("/mezzanine-quotations");
  };

  const openBom = async () => {
    setBomOpen(true);
    setBomBusy(true);
    setBomRows([]);
    try {
      const { data } = await api.get(`/mezzanine/quotations/${id}/bom`);
      setBomRows(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
      setBomOpen(false);
    } finally {
      setBomBusy(false);
    }
  };

  if (!q) return <Layout><div className="p-12 label-tech">Loading…</div></Layout>;

  return (
    <Layout>
      <div className="no-print">
        <PageHeader
          eyebrow={`Mezzanine · ${q.status}`}
          title={q.quote_number}
          subtitle={`Created ${new Date(q.created_at).toLocaleString("en-IN")} by ${q.created_by_name}`}
          actions={
            <div className="flex items-center gap-2">
              <button onClick={() => navigate(-1)} className="btn-ghost"><ArrowLeft size={16} /> Back</button>
              {canUseBom && (
                <button onClick={openBom} className="btn-secondary" data-testid="mz-detail-bom-btn">
                  <FileText size={16} /> BOM
                </button>
              )}
              <button onClick={() => window.print()} className="btn-primary"><Printer size={16} /> Print / PDF</button>
            </div>
          }
        />
        <div className="px-8 pt-6 flex flex-wrap items-center gap-2">
          <span className="label-tech mr-2">Actions</span>
          <button onClick={() => changeStatus("sent")} className="btn-secondary"><Send size={14} /> Mark Sent</button>
          <button onClick={() => changeStatus("approved")} className="btn-secondary"><CheckCheck size={14} /> Approved</button>
          <button onClick={() => changeStatus("rejected")} className="btn-secondary"><XIcon size={14} /> Rejected</button>
          {user.role === "admin" && <button onClick={del} className="btn-danger ml-auto"><Trash2 size={14} /> Delete</button>}
        </div>
      </div>

      <div className="p-8">
        <div className="panel-heavy max-w-5xl mx-auto" data-testid="mz-pdf">
          <div className="p-8 border-b-2 border-ink flex items-start justify-between">
            <div className="flex items-start gap-5">
              <img src="/brand/adarsh-logo.png" alt="Adarsh Udyog" className="h-20 w-auto object-contain" />
              <div>
                <div className="label-tech mt-1">A Complete Storage Solution</div>
                <div className="text-xs text-ink-muted mt-3 mono">
                  Plot No. 2, Nangli Sakrawati Industrial Area <br />
                  adarshudyog@gmail.com · +91 98100 98860
                  {q.company_gstin ? (<><br />GSTIN: {q.company_gstin}</>) : null}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="label-tech">Mezzanine Quotation</div>
              <div className="mono font-bold text-2xl mt-1">{q.quote_number}</div>
              <div className="label-tech mt-4">Date</div>
              <div className="mono text-sm mt-1">{new Date(q.created_at).toLocaleDateString("en-IN")}</div>
              <div className="label-tech mt-4">Status</div>
              <div className="mono font-bold uppercase">{q.status}</div>
            </div>
          </div>

          <div className="p-8 grid grid-cols-2 gap-8 border-b border-line">
            <div>
              <div className="label-tech mb-3">Billed to</div>
              <div className="font-bold text-ink">{q.client?.company || "—"}</div>
              <div className="text-sm text-ink-muted">{q.client?.name}</div>
              <div className="text-sm text-ink-muted">{q.client?.contact}</div>
              <div className="text-sm text-ink-muted">{q.client?.address}</div>
              {q.client?.gstin && <div className="mono text-xs mt-2">GSTIN: {q.client.gstin}</div>}
            </div>
            <div>
              <div className="label-tech mb-3">Specification</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <span className="text-ink-muted">Size (L×W×H)</span>
                <span className="mono font-bold text-right">{q.config.length_mm}×{q.config.width_mm}×{q.config.height_mm} mm</span>
                <span className="text-ink-muted">Area</span>
                <span className="mono font-bold text-right">{fmtNum(q.result.area_sqm, 2)} m² · {fmtNum(q.result.area_sqft, 0)} sqft</span>
                <span className="text-ink-muted">Load capacity</span>
                <span className="mono font-bold text-right">{q.config.load_capacity_kg_per_m2} kg/m²</span>
                <span className="text-ink-muted">V-to-V gap</span>
                <span className="mono font-bold text-right">{q.config.vv_gap_mm} mm</span>
                <span className="text-ink-muted">Staircases</span>
                <span className="mono font-bold text-right">{q.config.staircases}</span>
              </div>
            </div>
          </div>

          <div className="p-8">
            <div className="label-tech mb-3">Bill of materials</div>
            <table className="w-full grid-lines">
              <thead>
                <tr>
                  <th>Component</th>
                  <th>Section</th>
                  <th className="num">Approx unit wt</th>
                  <th className="num">Qty</th>
                  <th className="num">Total wt</th>
                </tr>
              </thead>
              <tbody>
                {q.result.components.map((c) => (
                  <tr key={c.component}>
                    <td className="font-bold uppercase tracking-wider text-xs">{c.component}</td>
                    <td className="mono text-xs">{c.section}</td>
                    <td className="num">{fmtNum(c.unit_weight_kg, 2)} kg</td>
                    <td className="num">{c.quantity}</td>
                    <td className="num font-bold">{fmtNum(c.total_weight_kg, 2)} kg</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Top-view SVG blueprint (same as configurator preview) */}
          {q.result?.geometry && (
            <div className="p-6 border-t border-line" data-testid="mz-detail-topview">
              <div className="label-tech mb-3">Top view · Layout Blueprint</div>
              <MezzanineTopView result={q.result} />
            </div>
          )}

          <div className="p-8 border-t-2 border-ink">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div>
                <div className="label-tech">Total Weight</div>
                <div className="mono text-2xl font-bold mt-1">{fmtNum(q.total_weight_kg, 2)} kg</div>
              </div>
              <div>
                <div className="label-tech">Rate</div>
                <div className="mono text-2xl font-bold mt-1">
                  ₹{parseFloat(Number(q.result.price_per_kg).toFixed(2))} / kg
                </div>
              </div>
              <div className="sm:text-right space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="label-tech">Subtotal</span>
                  <span className="mono font-bold">{fmtINR(q.total_price)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="label-tech">GST @ {fmtNum(q.gst_percent || 0, 0)}%</span>
                  <span className="mono font-bold">{fmtINR(q.gst_amount || 0)}</span>
                </div>
                {(q.freight_amount || 0) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="label-tech">Freight</span>
                    <span className="mono font-bold">{fmtINR(q.freight_amount)}</span>
                  </div>
                )}
                {(q.installation_amount || 0) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="label-tech">Installation</span>
                    <span className="mono font-bold">{fmtINR(q.installation_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t-2 border-ink">
                  <span className="label-tech">Grand Total</span>
                  <span className="display font-black text-3xl text-brand">{fmtINR(q.grand_total_price || q.total_price)}</span>
                </div>
              </div>
            </div>
          </div>

          {canEditCharges && (
            <ChargesEditor
              endpoint={`/mezzanine/quotations/${id}/charges`}
              freight={q.freight_amount}
              installation={q.installation_amount}
              onUpdated={setQ}
            />
          )}

          {q.notes && (
            <div className="p-8 pt-0 border-t border-line">
              <div className="label-tech mb-2">Notes</div>
              <div className="text-sm text-ink whitespace-pre-wrap">{q.notes}</div>
            </div>
          )}
          <div className="p-8 pt-4 border-t border-line">
            <div className="text-sm text-ink font-bold mb-2" data-testid="mz-extra-charges-note">
              Note: Fabrication charges for staircase and railing will be extra.
            </div>
            <div className="text-xs text-ink-muted">
              This quotation is valid for 7 days from the date of issue and is subject to final approval. Taxes as applicable.
            </div>
          </div>
        </div>
      </div>

      {bomOpen && (
        <PalletBomModal
          rows={bomRows}
          busy={bomBusy}
          client={q.client || {}}
          quoteNumber={q.quote_number}
          title="Mezzanine"
          items={[{ config: q.config, quantity: 1, label: q.client?.company || "" }]}
          onClose={() => setBomOpen(false)}
        />
      )}
    </Layout>
  );
}
